<template>
  <v-container class="quick-log" style="max-width: 640px;">
    <v-alert :type="alertType" v-if="alert" class="alert">{{
      alertMessage
    }}</v-alert>
    <v-card outlined>
      <v-card-title class="headline">Log an Interaction</v-card-title>
      <v-card-text>
        <v-autocomplete
          v-model="contactId"
          :items="contactOptions"
          label="Contact"
          placeholder="Who did you interact with?"
          outlined
          :loading="$apollo.loading"
          clearable
        ></v-autocomplete>

        <div class="overline mb-1">Interaction Type</div>
        <v-row dense class="mb-3">
          <v-col
            v-for="eventType in activeEventTypes"
            :key="eventType.id"
            cols="6"
            sm="4"
          >
            <v-btn
              block
              height="64"
              :color="eventTypeId === eventType.id ? 'primary' : ''"
              :outlined="eventTypeId !== eventType.id"
              @click="eventTypeId = eventType.id"
              class="event-type-btn"
            >
              <div>
                <div class="event-type-name">{{ eventType.name }}</div>
                <div class="caption">
                  {{ formatPoints(eventType.points) }}
                  {{ eventType.points === 1 ? "pt" : "pts" }}
                </div>
              </div>
            </v-btn>
          </v-col>
        </v-row>

        <div class="overline mb-1">When</div>
        <date-picker
          v-model="occurredAt"
          valueType="date"
          type="datetime"
          format="MMMM D YYYY, h:mm a"
          placeholder="Now"
          :minute-step="15"
          class="mb-4"
          style="width: 100%;"
        ></date-picker>

        <v-textarea
          v-model="comment"
          label="Comment (optional)"
          rows="2"
          outlined
          hide-details
        ></v-textarea>
      </v-card-text>
      <v-card-actions>
        <v-spacer></v-spacer>
        <v-btn
          color="primary"
          x-large
          :disabled="!contactId || !eventTypeId"
          :loading="saving"
          @click="logInteraction"
        >
          <v-icon left>mdi-check</v-icon>
          Log It
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import gql from "graphql-tag";
import sortBy from "lodash/sortBy";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";

export default {
  components: {
    DatePicker
  },
  data() {
    return {
      alert: false,
      alertType: false,
      alertMessage: false,
      contactId: null,
      eventTypeId: null,
      occurredAt: null,
      comment: "",
      saving: false
    };
  },
  created() {
    if (this.$route.query.contact) {
      this.contactId = this.$route.query.contact;
    }
  },
  apollo: {
    contacts: {
      query: gql`
        query QuickLogContacts {
          contacts {
            id
            name
          }
        }
      `
    },
    contactEventTypes: {
      query: gql`
        query QuickLogEventTypes {
          contactEventTypes {
            id
            name
            points
            active
          }
        }
      `
    },
    myCrmDashboard: {
      query: gql`
        query QuickLogMyContacts {
          myCrmDashboard {
            contacts {
              id
              name
              nextDue
              overdue
            }
          }
        }
      `
    }
  },
  computed: {
    activeEventTypes() {
      if (!this.contactEventTypes) return [];
      return sortBy(
        this.contactEventTypes.filter(t => t.active !== false),
        t => t.points
      );
    },
    contactOptions() {
      let all = this.contacts || [];
      let mine = this.myCrmDashboard ? this.myCrmDashboard.contacts : [];
      let myIds = new Set(mine.map(c => c.id));

      // My assigned contacts first (most urgent at the top), then everyone else
      let mineSorted = [...mine].sort((a, b) => {
        if (!a.nextDue) return 1;
        if (!b.nextDue) return -1;
        return new Date(a.nextDue) - new Date(b.nextDue);
      });

      let options = mineSorted.map(c => ({
        text: c.overdue ? `${c.name} (overdue)` : c.name,
        value: c.id
      }));

      let others = sortBy(
        all.filter(c => !myIds.has(c.id)),
        "name"
      ).map(c => ({ text: c.name, value: c.id }));

      if (options.length > 0 && others.length > 0) {
        return [
          { header: "My Contacts" },
          ...options,
          { divider: true },
          { header: "All Contacts" },
          ...others
        ];
      }
      return options.concat(others);
    }
  },
  methods: {
    formatPoints(points) {
      if (points == null) return 0;
      return points % 1 === 0 ? points : points.toFixed(1);
    },
    showAlert(type, message) {
      this.alert = true;
      this.alertType = type;
      this.alertMessage = message;
      setTimeout(() => {
        this.alert = false;
      }, 3000);
    },
    logInteraction() {
      this.saving = true;
      let occurredAt = this.occurredAt
        ? this.occurredAt.toISOString()
        : new Date().toISOString();

      this.$apollo
        .mutate({
          mutation: gql`
            mutation LogInteraction(
              $contactId: Int!
              $contactEventTypeId: Int!
              $occurredAt: DateTime
              $comment: String
            ) {
              logInteraction(
                contactId: $contactId
                contactEventTypeId: $contactEventTypeId
                occurredAt: $occurredAt
                comment: $comment
              ) {
                id
                points
              }
            }
          `,
          variables: {
            contactId: parseInt(this.contactId, 10),
            contactEventTypeId: parseInt(this.eventTypeId, 10),
            occurredAt,
            comment: this.comment || null
          }
        })
        .then(response => {
          this.saving = false;
          let points = response.data.logInteraction.points;
          this.showAlert(
            "success",
            `Interaction logged! +${this.formatPoints(points)} points`
          );
          this.contactId = null;
          this.eventTypeId = null;
          this.occurredAt = null;
          this.comment = "";
        })
        .catch(error => {
          this.saving = false;
          console.error(error);
          this.showAlert("error", error.message);
        });
    }
  }
};
</script>

<style scoped lang="scss">
.alert {
  position: fixed;
  left: 50%;
  bottom: 50px;
  transform: translate(-50%, -50%);
  margin: 0 auto;
  z-index: 10;
}
.event-type-btn {
  text-transform: none;
}
.event-type-name {
  font-size: 13px;
  white-space: normal;
  line-height: 1.2;
}
</style>
